import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, Sparkles, MapPin } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { scrollToSection } from "@/components/Navbar";

const HERO_IMG =
  "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwbGl2aW5nJTIwcm9vbSUyMGJyaWdodHxlbnwwfHx8fDE3ODc1ODg3ODl8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const frameRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: frameRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const badgeY = useTransform(scrollYProgress, [0, 1], ["12%", "-12%"]);

  return (
    <section
      className="relative min-h-[94vh] flex flex-col justify-center pt-36 pb-20 overflow-hidden"
      data-testid="hero-section"
    >
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-foam/25 blur-3xl pointer-events-none"
      />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 w-full grid grid-cols-1 md:grid-cols-12 gap-14 md:gap-10 items-center">
        <div className="md:col-span-7 relative z-10">
          <MaskedLine delay={0.35}>
            <span className="inline-flex items-center gap-2 text-[11px] md:text-xs uppercase tracking-[0.28em] text-seateal font-medium">
              <MapPin size={12} strokeWidth={2} /> Knoxville, Tennessee
            </span>
          </MaskedLine>

          <h1 className="mt-7 font-display font-light tracking-tighter leading-[0.95] text-pine text-[17vw] md:text-[7.2rem]">
            <MaskedLine delay={0.5}>Clean Spaces.</MaskedLine>
            <MaskedLine delay={0.68}>
              <span className="font-editorial italic font-semibold text-seateal">Happy Places.</span>
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.95}>
            <p className="mt-8 max-w-md text-base md:text-lg font-light leading-relaxed text-pine/70">
              Professional cleaning, exceptional results. Reliable, thorough and trusted care for
              Knoxville homes, short-term rentals and businesses.
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-11 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToSection("#contact")}
              className="group rounded-full bg-foam text-pine pl-7 pr-3 py-2.5 font-display font-medium tracking-wide flex items-center gap-4 hover:bg-foamdeep transition-colors duration-300"
              data-testid="hero-quote-cta"
            >
              Get a Free Quote
              <span className="w-9 h-9 rounded-full bg-pine text-foam flex items-center justify-center transition-transform duration-500 group-hover:translate-y-0.5">
                <ArrowDown size={15} strokeWidth={2} />
              </span>
            </button>
            <a
              href="tel:+18654550375"
              className="rounded-full border border-pine/20 text-pine px-7 py-3 font-display font-normal tracking-wide flex items-center gap-2.5 hover:border-foamdeep hover:bg-white/60 transition-colors duration-300"
              data-testid="hero-phone-cta"
            >
              <Phone size={15} strokeWidth={1.75} /> (865) 455-0375
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="mt-12 font-editorial italic text-lg text-pine/50"
            data-testid="hero-tagline"
          >
            “We clean so you can enjoy what matters most.”
          </motion.p>
        </div>

        <div className="md:col-span-5 relative" ref={frameRef}>
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.3, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(2,44,34,0.12)] aspect-[4/5]"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={HERO_IMG}
              alt="A bright, freshly cleaned modern living room"
              style={{ y }}
              className="absolute inset-0 w-full h-[124%] object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-pine/25 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            style={{ y: badgeY }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-6 -left-4 md:-left-10 bg-white/80 backdrop-blur-xl border border-white/50 rounded-2xl px-5 py-4 shadow-[0_16px_50px_rgba(2,44,34,0.10)] flex items-center gap-3"
            data-testid="hero-turno-badge"
          >
            <span className="w-10 h-10 rounded-full bg-foam flex items-center justify-center text-pine">
              <Sparkles size={17} strokeWidth={1.75} />
            </span>
            <span className="text-sm leading-snug">
              <span className="block font-display font-medium text-pine">5-Star STR Turnovers</span>
              <span className="block text-pine/60 font-light text-xs mt-0.5">Bookable on Turno</span>
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
